import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Error handler function - will be set by App.js
let globalErrorHandler = null;

export const setGlobalErrorHandler = (handler) => {
  globalErrorHandler = handler;
};

// Request interceptor
api.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors globally
api.interceptors.response.use(
  (response) => {
    // Check if response is HTML instead of JSON (common when API route doesn't exist)
    const contentType = response.headers['content-type'] || '';
    const responseData = response.data;
    
    // Check both content-type header and response data content
    const isHTML = contentType.includes('text/html') || 
                   (typeof responseData === 'string' && 
                    (responseData.trim().startsWith('<!') || 
                     responseData.includes('JavaScript Required') ||
                     responseData.includes('<html') ||
                     responseData.includes('<noscript>')));
    
    if (isHTML) {
      let errorMessage = 'The API endpoint returned an HTML page instead of JSON.';
      if (typeof responseData === 'string' && responseData.includes('JavaScript Required')) {
        errorMessage = 'The backend API is not accessible. The server returned the frontend HTML page instead of the API response.';
      }
      
      if (globalErrorHandler) {
        globalErrorHandler({
          title: 'Backend API Not Available',
          message: errorMessage,
          details: `This usually means:\n1. The backend server is not running\n2. The API route does not exist\n3. The request was routed to the frontend instead\n\nURL: ${response.config?.url || 'Unknown'}\nBase URL: ${API_BASE_URL}\nExpected: JSON response\nReceived: HTML response`,
          type: 'error',
        });
      }
      return Promise.reject(new Error('API returned HTML instead of JSON'));
    }
    return response;
  },
  (error) => {
    // Handle network errors
    if (!error.response) {
      const errorMessage = error.message || 'Network error. Please check your internet connection.';
      if (globalErrorHandler) {
        globalErrorHandler({
          title: 'Network Error',
          message: errorMessage,
          details: `Unable to reach the server at ${API_BASE_URL}. Please ensure:\n1. The backend server is running\n2. The API URL is correct\n3. There are no firewall or CORS issues`,
          type: 'error',
        });
      }
      return Promise.reject(error);
    }

    const { status, data, headers } = error.response;
    const contentType = headers['content-type'] || '';
    
    // Check if error response is HTML (common when API route doesn't exist or backend is down)
    const isHTML = contentType.includes('text/html') || 
                   (typeof data === 'string' && 
                    (data.trim().startsWith('<!') || 
                     data.includes('JavaScript Required') ||
                     data.includes('<html') ||
                     data.includes('<noscript>')));
    
    if (isHTML) {
      let errorMessage = 'The API endpoint returned an HTML page instead of JSON.';
      if (typeof data === 'string' && data.includes('JavaScript Required')) {
        errorMessage = 'The backend API is not accessible. The server returned the frontend HTML page instead of the API response.';
      }
      
      if (globalErrorHandler) {
        globalErrorHandler({
          title: 'Backend API Not Available',
          message: errorMessage,
          details: `This usually means:\n1. The backend server is not running\n2. The API route does not exist\n3. The request was routed to the frontend instead\n\nURL: ${error.config?.url || 'Unknown'}\nBase URL: ${API_BASE_URL}\nStatus: ${status}\nExpected: JSON response\nReceived: HTML response`,
          type: 'error',
        });
      }
      return Promise.reject(error);
    }

    let title = 'API Error';
    let message = data?.error || data?.message || 'An unexpected error occurred';
    let type = 'error';
    let details = null;

    // Handle specific status codes
    switch (status) {
      case 401:
        title = 'Authentication Required';
        message = 'Your session has expired. Please log in again.';
        type = 'warning';
        details = data;
        // Optionally redirect to login
        if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
          setTimeout(() => {
            window.location.href = '/';
          }, 2000);
        }
        break;
      case 403:
        title = 'Access Forbidden';
        message = 'You do not have permission to access this resource.';
        type = 'warning';
        details = data;
        break;
      case 404:
        title = 'Not Found';
        message = 'The requested resource was not found.';
        type = 'info';
        details = data;
        break;
      case 500:
        title = 'Server Error';
        message = 'An internal server error occurred. Please try again later.';
        type = 'error';
        details = data;
        break;
      case 503:
        title = 'Service Unavailable';
        message = 'The service is temporarily unavailable. Please try again later.';
        type = 'warning';
        details = data;
        break;
      default:
        if (status >= 400 && status < 500) {
          title = 'Client Error';
          type = 'warning';
        } else if (status >= 500) {
          title = 'Server Error';
          type = 'error';
        }
        details = data;
    }

    // Show error modal if handler is available
    if (globalErrorHandler) {
      globalErrorHandler({
        title,
        message,
        details: details ? JSON.stringify(details, null, 2) : null,
        type,
      });
    }

    return Promise.reject(error);
  }
);

// Email API
export const emailAPI = {
  sendEmail: (data) => api.post('/email/send', data),
  sendBulkEmails: (data) => api.post('/email/send-bulk', data),
  sendBoardingPass: (data) => api.post('/email/boarding-pass', data),
  getHistory: (params) => api.get('/email/history', { params }),
  getEmailById: (id) => api.get(`/email/${id}`),
  getAirlines: () => api.get('/email/airlines'),
  getCheckinUrl: (airlineName, pnr) => api.get('/email/airlines/checkin-url', { 
    params: { airlineName, pnr } 
  }),
  searchAirlines: (query) => api.get('/email/airlines/search', { 
    params: { q: query } 
  }),
};

// Templates API
export const templatesAPI = {
  getAll: () => api.get('/templates'),
  getById: (id) => api.get(`/templates/${id}`),
  create: (data) => api.post('/templates', data),
  update: (id, data) => api.put(`/templates/${id}`, data),
  delete: (id) => api.delete(`/templates/${id}`),
};

// Stats API
export const statsAPI = {
  getStats: (params) => api.get('/stats', { params }),
};

// Jobs API
export const jobsAPI = {
  uploadCSV: (formData) => api.post('/jobs/upload-csv', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  getJob: (jobId) => api.get(`/jobs/${jobId}`),
  getAllJobs: () => api.get('/jobs'),
  resumeJob: (jobId) => api.post(`/jobs/${jobId}/resume`),
};

// Database API
export const databaseAPI = {
  getBrands: () => api.get('/database/brands'),
  testConnection: () => api.get('/database/test-connection'),
  getEmailCount: (brandId) => api.get(`/database/email-count/${brandId}`),
  getBrandSender: (brandId) => api.get(`/database/brand-sender/${brandId}`),
  createJob: (data) => api.post('/database/create-job', data),
};

// Push Notification API
export const pushAPI = {
  sendPush: (data) => api.post('/push/send', data),
  sendBulkPush: (data) => api.post('/push/send-bulk', data),
  getStatus: (userEmail) => api.get('/push/status', { params: { userEmail } }),
};

// Profile API
export const profileAPI = {
  getProfile: (email) => api.get('/profile', { params: { email } }),
  uploadFirebase: (formData) => api.post('/profile/upload-firebase', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  deleteFirebaseConfig: (email) => api.delete('/profile/firebase-config', { params: { email } }),
};

// Webhook Logs API
export const webhookLogsAPI = {
  getLogs: (params) => api.get('/webhook-logs', { params }),
  getLogById: (id) => api.get(`/webhook-logs/${id}`),
};

export default api;

